/**
 * Компонент статуса Telegram Client
 * Дата: 30.12.2025
 * Назначение: Отображение текущего статуса подключения Telegram Client
 * Обновлено: 30.12.2025 - Заменены alert/confirm на Modal/ConfirmDialog, добавлена проверка permissions
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Card, CardHeader, CardTitle, CardContent, ConfirmDialog } from '@/components/ui';
import { checkAuthStatus, disconnect, type AuthStatus } from '@/api/telegramClient';
import { CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/useToast';

interface TelegramClientStatusProps {
  onDisconnect?: () => void;
  tenantId?: string; // Для SuperAdmin: передача tenantId для запросов
}

export function TelegramClientStatus({ onDisconnect, tenantId }: TelegramClientStatusProps) {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { can } = usePermissions();
  const { showError, showSuccess } = useToast();
  
  // Проверка прав на управление Telegram Client
  const canManage = can('telegram_client:manage') || can('settings:write');

  const loadStatus = async () => {
    // Не делать запрос если tenantId не указан
    if (!tenantId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const currentStatus = await checkAuthStatus(tenantId);
      setStatus(currentStatus);
    } catch (err) {
      console.error('Failed to load status:', err);
      setStatus({ status: 'disconnected' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // Обновлять статус каждые 30 секунд (только если tenantId указан)
    if (!tenantId) return;
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [tenantId]);

  const handleDisconnectClick = () => {
    setShowConfirmDialog(true);
  };

  const handleDisconnect = async () => {
    setShowConfirmDialog(false);
    try {
      setDisconnecting(true);
      await disconnect(tenantId);
      await loadStatus();
      onDisconnect?.();
      showSuccess('Telegram аккаунт успешно отключен');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Не удалось отключить аккаунт');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = () => {
    switch (status?.status) {
      case 'connected':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Подключен
          </Badge>
        );
      case 'auth_required':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Требуется авторизация
          </Badge>
        );
      case '2fa_required':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Требуется 2FA
          </Badge>
        );
      default:
        return (
          <Badge variant="error" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Не подключен
          </Badge>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Telegram Аккаунт (Client)</CardTitle>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent>
        {status?.status === 'connected' && (
          <div className="space-y-3">
            {status.phone && (
              <p className="text-sm text-neutral-600">
                <span className="font-medium">Телефон:</span> {status.phone}
              </p>
            )}
            
            {/* Счётчик синхронизированных сообщений */}
            {typeof status.messages_count === 'number' && (
              <p className="text-sm text-neutral-600">
                <span className="font-medium">Всего сообщений:</span>{' '}
                <span className="text-primary-600 font-semibold">{status.messages_count.toLocaleString('ru-RU')}</span>
              </p>
            )}
            
            {/* Кнопка отключения */}
            {canManage && (
              <div className="pt-2">
                <Button
                  onClick={handleDisconnectClick}
                  variant="danger"
                  size="sm"
                  isLoading={disconnecting}
                  disabled={disconnecting}
                >
                  Отключить аккаунт
                </Button>
              </div>
            )}
          </div>
        )}
        {status?.status === 'auth_required' && (
          <p className="text-sm text-neutral-600">
            Подключите Telegram аккаунт через QR-код для использования функций поиска по телефону.
          </p>
        )}
        {status?.status === '2fa_required' && (
          <div className="space-y-2">
            <p className="text-sm text-neutral-600">
              У вашего аккаунта включена двухфакторная аутентификация.
            </p>
            {status.password_hint && (
              <p className="text-xs text-neutral-500">
                Подсказка: {status.password_hint}
              </p>
            )}
          </div>
        )}
        {status?.status === 'disconnected' && (
          <p className="text-sm text-neutral-600">
            Telegram аккаунт не подключен.
          </p>
        )}
      </CardContent>
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleDisconnect}
        title="Отключение Telegram аккаунта"
        message="Вы уверены, что хотите отключить Telegram аккаунт?"
        confirmText="Отключить"
        cancelText="Отмена"
        variant="danger"
        isLoading={disconnecting}
      />
    </Card>
  );
}

